import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfPen,
  PdfColor,
  PdfPoint,
} from '../../pdf/pdf';
import { parseDot } from './parser';
import { layoutGraph, layoutGraphWithSizes, computeBoundingBox } from './layout';
import { GraphLayout, LayoutNode, LayoutEdge, LayoutCluster, NodeShape, ArrowType } from './types';

const NAMED_COLORS: Record<string, string> = {
  lightgrey: '#d3d3d3',
  lightgray: '#d3d3d3',
  grey: '#808080',
  gray: '#808080',
  black: '#000000',
  white: '#ffffff',
  red: '#ff0000',
  green: '#00ff00',
  blue: '#0000ff',
  yellow: '#ffff00',
  cyan: '#00ffff',
  magenta: '#ff00ff',
  purple: '#800080',
  orange: '#ffa500',
  brown: '#a52a2a',
};

function normalizeColor(color: string): string {
  if (color.startsWith('#')) return color;
  const lower = color.toLowerCase();
  return NAMED_COLORS[lower] || color;
}

export const dotModule: IModule = {
  name: 'dot',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    return { type: 'dot', attrs, content: source };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    try {
      const graph = parseDot(content.content);
      const initialLayout = layoutGraph(graph);

      const nodeSizes = new Map<string, { width: number; height: number }>();
      for (const node of initialLayout.nodes) {
        const font = new PdfFont(PdfFont.FONT_NAME, node.style.fontsize || 14);
        const measured = params.page
          ? params.page.measureContent(node.label, { font, page: params.page })
          : { width: node.width, height: node.height };
        nodeSizes.set(node.id, { width: measured.width, height: measured.height });
      }

      const layout = layoutGraphWithSizes(graph, nodeSizes);
      return new PdfSize(layout.boundingBox.width, layout.boundingBox.height);
    } catch (e) {
      console.error('DOT module measure error:', e);
      return new PdfSize(100, 50);
    }
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) {
      throw new Error('PageGen required for drawing');
    }

    try {
      const graph = parseDot(content.content);

      // First pass: get initial layout to have nodes to measure
      const initialLayout = layoutGraph(graph);

      // Measure each node's label
      const nodeSizes = new Map<string, { width: number; height: number }>();
      for (const node of initialLayout.nodes) {
        const font = new PdfFont(PdfFont.FONT_NAME, node.style.fontsize || 14);
        const measured = page.measureContent(node.label, { font, page });
        nodeSizes.set(node.id, { width: measured.width, height: measured.height });
      }

      // Second pass: layout with measured sizes
      const layout = layoutGraphWithSizes(graph, nodeSizes);

      renderGraph(page, layout, rect, align, params);
    } catch (e) {
      console.error('DOT module draw error:', e);
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
      page.drawString(`Error: ${(e as Error).message}`, font, PdfBrush.Black, null, rect, align);
    }
  },
};

function renderGraph(
  page: PageGen,
  layout: GraphLayout,
  rect: PdfRect,
  align: ETextAlign,
  params: ModuleParams
): void {
  // PdfRect uses top-left coordinates (Y increases downward)
  // Our layout also uses Y increasing downward
  // So we can use rect directly

  const scale = Math.min(
    rect.width / Math.max(layout.boundingBox.width, 1),
    rect.height / Math.max(layout.boundingBox.height, 1)
  );

  // Center the graph in the rectangle
  const graphWidth = layout.boundingBox.width * scale;
  const graphHeightVal = layout.boundingBox.height * scale;

  // Adjust for graph's min coordinates first (to prevent negative coordinates)
  let offsetX = rect.left - layout.boundingBox.minX * scale;
  let offsetY = rect.top - layout.boundingBox.minY * scale;

  // Adjust for alignment AFTER accounting for min coordinates
  if (
    align === ETextAlign.Center ||
    align === ETextAlign.TopCenter ||
    align === ETextAlign.BottomCenter
  ) {
    offsetX = rect.left + (rect.width - graphWidth) / 2 - layout.boundingBox.minX * scale;
  } else if (
    align === ETextAlign.BottomRight ||
    align === ETextAlign.CenterRight ||
    align === ETextAlign.TopRight
  ) {
    offsetX = rect.right - graphWidth - layout.boundingBox.minX * scale;
  }

  if (
    align === ETextAlign.Center ||
    align === ETextAlign.CenterLeft ||
    align === ETextAlign.CenterRight
  ) {
    offsetY = rect.top + (rect.height - graphHeightVal) / 2 - layout.boundingBox.minY * scale;
  } else if (
    align === ETextAlign.TopLeft ||
    align === ETextAlign.TopCenter ||
    align === ETextAlign.TopRight
  ) {
    offsetY = rect.top - layout.boundingBox.minY * scale;
  } else if (
    align === ETextAlign.BottomLeft ||
    align === ETextAlign.BottomCenter ||
    align === ETextAlign.BottomRight
  ) {
    offsetY = rect.bottom - graphHeightVal - layout.boundingBox.minY * scale;
  }

  // No need to invert Y axis - both use Y increasing downward
  // Draw functions expect top-left coordinates
  for (const cluster of layout.clusters) {
    drawCluster(page, cluster, scale, offsetX, offsetY);
  }

  for (const edge of layout.edges) {
    drawEdge(page, edge, scale, offsetX, offsetY);
  }

  for (const node of layout.nodes) {
    drawNode(page, node, scale, offsetX, offsetY, params);
  }
}

function drawCluster(
  page: PageGen,
  cluster: LayoutCluster,
  scale: number,
  offsetX: number,
  offsetY: number
): void {
  const x = cluster.rect.x * scale + offsetX;
  const y = cluster.rect.y * scale + offsetY;
  const w = cluster.rect.width * scale;
  const h = cluster.rect.height * scale;

  const pen = cluster.style.stroke
    ? new PdfPen(PdfColor.fromHex(normalizeColor(cluster.style.stroke)), 1)
    : null;

  const brush = cluster.style.fill
    ? new PdfBrush(PdfColor.fromHex(normalizeColor(cluster.style.fill)))
    : null;

  if (pen || brush) {
    page.drawRectangle(pen, brush, new PdfRect(x, y, w, h));
  }
}

function drawNode(
  page: PageGen,
  node: LayoutNode,
  scale: number,
  offsetX: number,
  offsetY: number,
  params: ModuleParams
): void {
  const x = node.x * scale + offsetX;
  const y = node.y * scale + offsetY;
  const w = node.width * scale;
  const h = node.height * scale;

  const rect = new PdfRect(x - w / 2, y - h / 2, w, h);

  const fillColor = node.style.fill;
  const strokeColor = node.style.stroke;

  const pen = strokeColor
    ? new PdfPen(PdfColor.fromHex(normalizeColor(strokeColor)), 1)
    : new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);

  const brush = fillColor
    ? new PdfBrush(PdfColor.fromHex(normalizeColor(fillColor)))
    : new PdfBrush(PdfColor.fromRgb(255, 255, 255));

  switch (node.shape) {
    case 'box':
    case 'Msquare':
      page.drawRectangle(pen, brush, rect);
      break;
    case 'ellipse':
    case 'circle':
      page.drawEllipse(pen, brush, rect);
      break;
    case 'diamond':
    case 'Mdiamond':
      drawDiamond(page, pen, brush, rect);
      break;
    case 'round':
      page.drawRoundedRectangle(pen, brush, rect, 8);
      break;
    case 'plaintext':
      break;
  }

  const font = new PdfFont(PdfFont.FONT_NAME, node.style.fontsize || 14);
  page.drawContent(node.label, rect, ETextAlign.Center, { font, page });
}

function drawDiamond(page: PageGen, pen: PdfPen, brush: PdfBrush, rect: PdfRect): void {
  const points = [
    new PdfPoint(rect.centerX, rect.top),
    new PdfPoint(rect.right, rect.centerY),
    new PdfPoint(rect.centerX, rect.bottom),
    new PdfPoint(rect.left, rect.centerY),
  ];

  const allPoints = [points[0], points[1], points[2], points[3], points[0]];
  page.drawLines(pen, allPoints);
}

function drawEdge(
  page: PageGen,
  edge: LayoutEdge,
  scale: number,
  offsetX: number,
  offsetY: number
): void {
  if (edge.points.length < 2) return;

  const scaledPoints = edge.points.map(
    (p) => new PdfPoint(p.x * scale + offsetX, p.y * scale + offsetY)
  );

  const pen = new PdfPen(
    PdfColor.fromHex(normalizeColor(edge.style.stroke || '#000000')),
    (edge.style.strokeWidth || 1) * scale
  );

  if (edge.style.strokeDash) {
    pen.dashArray = edge.style.strokeDash.map((d) => d * scale);
  }

  // If we have 4+ points, use bezier curves (spline mode)
  // Format: [start, c1, c2, end] for a single cubic bezier
  if (scaledPoints.length >= 4) {
    page.drawBeziers(pen, null, scaledPoints);
  } else {
    // Fallback to straight lines for orthogonal edges
    for (let i = 0; i < scaledPoints.length - 1; i++) {
      page.drawLine(pen, scaledPoints[i], scaledPoints[i + 1]);
    }
  }

  if (edge.style.arrowhead && edge.style.arrowhead !== 'none') {
    const last = scaledPoints[scaledPoints.length - 1];
    const prev = scaledPoints[scaledPoints.length - 2];
    drawArrowhead(page, prev, last, edge.style.arrowhead, pen);
  }
}

function drawArrowhead(
  page: PageGen,
  from: PdfPoint,
  to: PdfPoint,
  type: ArrowType,
  pen: PdfPen
): void {
  const size = 8;
  const angle = Math.atan2(to.y - from.y, to.x - from.x);

  const points: PdfPoint[] = [];

  switch (type) {
    case 'normal':
    case 'vee':
      points.push(
        new PdfPoint(
          to.x - size * Math.cos(angle - Math.PI / 6),
          to.y - size * Math.sin(angle - Math.PI / 6)
        ),
        to,
        new PdfPoint(
          to.x - size * Math.cos(angle + Math.PI / 6),
          to.y - size * Math.sin(angle + Math.PI / 6)
        )
      );
      break;
    default:
      points.push(
        new PdfPoint(
          to.x - size * Math.cos(angle - Math.PI / 6),
          to.y - size * Math.sin(angle - Math.PI / 6)
        ),
        to,
        new PdfPoint(
          to.x - size * Math.cos(angle + Math.PI / 6),
          to.y - size * Math.sin(angle + Math.PI / 6)
        )
      );
  }

  page.drawLines(pen, points);
}
